<template>
  <div class="min-h-screen bg-gray-100">
    <div class="mx-auto p-6">
      <div
        class="flex justify-between items-end mb-6 mdmax:flex-col mdmax:items-stretch gap-4"
      >
        <div class="flex-1 mdmax:w-full">
          <div class="max-w-lg">
            <label
              class="text-base text-neutral-700 font-medium mb-1 inline-block"
            >
              Pencarian
            </label>
            <div class="relative">
              <TextField
                type="text"
                :withIcon="true"
                placeholder="Cari banner..."
                v-model="search"
                @enter="onSearch"
              />
              <div class="absolute left-3 top-1/2 transform -translate-y-1/2">
                <Search />
              </div>
            </div>
          </div>
        </div>

        <div class="flex mdmax:w-full">
          <button
            @click="$router.push('/banner/create')"
            class="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 font-medium"
          >
            + Tambah Banner
          </button>
        </div>
      </div>

      <TableComponent :columns="columns" :list="tableData">
        <template #table-row="{ entry, column }">
          <span
            v-if="column.field === 'action'"
            class="flex items-center gap-3"
          >
            <button
              @click="$router.push(`/banner/edit/${entry.id}`)"
              class="text-neutral-500 hover:text-primary-500"
              title="Edit"
            >
              <Pencil width="18" height="18" />
            </button>
            <button
              @click="onDelete(entry)"
              class="text-neutral-500 hover:text-red-500"
              title="Delete"
            >
              <Trash width="18" height="18" />
            </button>
          </span>

          <span v-else-if="column.field === 'RowNumber'" class="font-medium">
            {{ entry.RowNumber }}
          </span>

          <span v-else-if="column.field === 'imageURL'">
            <img
              v-if="entry.imageURL"
              :src="entry.imageURL"
              alt="Banner Image"
              class="w-20 h-12 object-cover rounded"
            />
          </span>

          <span v-else-if="column.field === 'isActive'">
            <span
              class="px-2 py-1 rounded text-xs font-medium"
              :class="statusClass(entry.isActive)"
            >
              {{ entry.isActive ? "Active" : "Inactive" }}
            </span>
          </span>

          <span v-else class="text-sm text-gray-700">
            {{ entry[column.field] }}
          </span>
        </template>
      </TableComponent>

      <div
        v-if="tableData.length > 0"
        class="mt-6 mb-6 flex justify-between items-center mdmax:flex-col mdmax:items-start gap-4"
      >
        <div v-if="!isMobile" class="text-sm font-medium text-neutral-500">
          Menampilkan {{ start }} - {{ end }} dari {{ totalRecords }}
        </div>

        <Pagination
          :currentPage="page"
          :totalCount="totalRecords"
          :pageSize="limit"
          @onPageChange="changePage"
        />
      </div>

      <Modal
        :modalVisible="deleteModalVisible"
        @close="closeDeleteModal"
        width="400px"
      >
        <div slot="modal-content" class="text-center">
          <h3 class="text-lg font-bold text-neutral-800 mb-2">Hapus Banner</h3>
          <p class="text-neutral-600 mb-6">
            Apakah Anda yakin ingin menghapus banner
            <strong>{{ bannerToDelete?.name }}</strong
            >?
          </p>
          <div class="flex justify-center gap-3">
            <button
              @click="closeDeleteModal"
              class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-600 hover:bg-neutral-50 font-medium"
            >
              Batal
            </button>
            <button
              @click="confirmDelete"
              class="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 font-medium"
            >
              Hapus
            </button>
          </div>
        </div>
      </Modal>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import TableComponent from "@/components/Table/Table"
import Pagination from "@/components/Pagination/Pagination"
import TextField from "@/components/Forms/TextField"
import Pencil from "@/components/Icons/Pencil"
import Trash from "@/components/Icons/Trash"
import Modal from "@/components/Modal/Modal"
import Search from "@/components/Icons/Search"

export default {
  name: "BannerList",
  components: {
    TableComponent,
    Pagination,
    TextField,
    Pencil,
    Trash,
    Modal,
    Search
  },

  data() {
    return {
      search: "",
      page: 1,
      limit: 10,
      deleteModalVisible: false,
      bannerToDelete: null,
      columns: [
        { label: "No", field: "RowNumber" },
        { label: "Nama Banner", field: "name" },
        { label: "Lokasi", field: "location" },
        { label: "Posisi", field: "position" },
        { label: "Gambar", field: "imageURL" },
        { label: "Status", field: "isActive" },
        { label: "Action", field: "action" }
      ]
    }
  },

  computed: {
    ...mapGetters("banner", ["bannerList"]),
    ...mapGetters("common", ["isMobile"]),

    totalRecords() {
      return this.bannerList.meta?.total_item || 0
    },

    tableData() {
      if (!this.bannerList.data) return []
      return this.bannerList.data.map((banner, index) => ({
        ...banner,
        RowNumber: (this.page - 1) * this.limit + index + 1
      }))
    },

    start() {
      return this.totalRecords ? (this.page - 1) * this.limit + 1 : 0
    },

    end() {
      return Math.min(this.page * this.limit, this.totalRecords)
    }
  },

  methods: {
    ...mapActions("banner", ["getBanners", "deleteBanner"]),
    ...mapActions("common", ["showLoading", "hideLoading"]),

    statusClass(isActive) {
      return isActive
        ? "bg-green-100 text-green-700"
        : "bg-gray-100 text-gray-700"
    },

    fetchBanners() {
      this.showLoading()
      const params = {
        page: this.page,
        per_page: this.limit,
        keyword: this.search
      }
      this.getBanners({ params }).finally(() => this.hideLoading())
    },

    onSearch() {
      this.page = 1
      this.fetchBanners()
    },

    changePage(val) {
      this.page = val
      this.fetchBanners()
    },

    onDelete(banner) {
      this.bannerToDelete = banner
      this.deleteModalVisible = true
    },

    closeDeleteModal() {
      this.deleteModalVisible = false
      this.bannerToDelete = null
    },

    async confirmDelete() {
      if (!this.bannerToDelete) return

      this.showLoading()
      try {
        await this.deleteBanner({ id: this.bannerToDelete.id })

        this.$toast?.success("Banner berhasil dihapus")
        this.fetchBanners()
      } catch (err) {
        console.error(err)
        this.$toast?.error(
          err.response?.data?.message || "Gagal menghapus banner"
        )
      } finally {
        this.hideLoading()
        this.closeDeleteModal()
      }
    }
  },

  mounted() {
    this.fetchBanners()
  }
}
</script>

<style scoped>
img {
  object-fit: cover;
}
</style>
